/**
 * @module app.printDirective
 */
import {unByKey} from 'ol/Observable.js';
import appBase from './index.js';
import {POINTS_PER_METER} from './constants.js';


/**
 * @param {angular.Scope} $scope Scope.
 * @param {angularGettext.Catalog} gettextCatalog Gettext catalog.
 * @param {app.MapService} appMapService
 * @param {app.PrintHelper} appPrintHelper
 * @param {app.track.service} appTrackService
 * @struct
 * @constructor
 * @ngInject
 * @private
 */
class PrintController {

  constructor($scope, gettextCatalog, appMapService, appPrintHelper, appTrackService) {
    /**
     * @type {angular.Scope}
     * @private
     */
    this.scope_ = $scope;

    /**
     * @private
     */
    this.trackService_ = appTrackService;

    /**
     * @type {boolean}
     * @export
     */
    this.inTracks;

    /**
     * @type {string}
     * @export
     */
    this.parent;

    /**
     * @type {ol.Map}
     * @export
     */
    this.map = appMapService.map;

    /**
     * @type {angularGettext.Catalog}
     */
    this.gettextCatalog = gettextCatalog;

    /**
     * @type {Array.<number>}
     * @export
     */
    this.scales = appPrintHelper.SCALES;

    /**
     * @type {number|undefined}
     * @export
     */
    this.scale = undefined;

    /**
     * @type {string}
     * @export
     */
    this.orientation = 'landscape';

    /**
     * @private
     * @type {ol.EventsKey|Array.<ol.EventsKey>}
     */
    this.postcomposeListenerKey_ = null;

    /**
     * @private
     */
    this.helper_ = appPrintHelper;

  }
  $onDestroy() {
    this.togglePrint_(false);
  }

  /**
   *
   */
  $onInit() {
    const collapser = $('#' + this.parent).find('[app-collapse-menu]');
    console.assert(collapser.length === 1);
    this.scope_.$on('submenuChange', (event, data) => {
      data = /** @type {appx.SubmenuChangeEvent} */ (data);
      if (collapser.is(data.element)) {
        this.togglePrint_(data.open);
      }
    });

    this.togglePrint_(this.inTracks);
  }


  /**
   * @private
   * @param {boolean} active Status of the printing tool
   */
  togglePrint_(active) {
    if (active) {
      console.assert(!this.postcomposeListenerKey_);
      const postcompose = this.postcompose_.bind(this);
      this.postcomposeListenerKey_ = this.map.on('postcompose', postcompose);

      this.scale = this.getOptimalScale_();
    } else {
      unByKey(this.postcomposeListenerKey_);
      this.postcomposeListenerKey_ = null;
    }
    this.map.render();
  }


  /**
   * @private
   * @return {string}
   */
  getLayout_() {
    const layout = ['A4', this.orientation];
    const layers = this.map.getLayers().getArray();
    const isSlowupLayerActive = layers.some(layer => (
      layer.get('layername') === 'Slowup' && layer.getVisible()
    ));
    if (this.inTracks) {
      layout.push('plus');
    } else if (isSlowupLayerActive) {
      layout.push('slowUp');
    }
    layout.push(this.gettextCatalog.currentLanguage);

    const layout_name = layout.join(' ');
    console.assert(this.helper_.getPaperSize(layout_name));

    return layout_name;
  }


  /**
   * @export
   */
  scaleChanged() {
    this.map.render();
  }


  /**
   * @export
   */
  orientationChanged() {
    this.map.render();
  }


  /**
   * @param {ol.render.Event} event
   * @private
   */
  postcompose_(event) {
    const viewport = event.target.getViewport();
    const canvases = viewport.getElementsByTagName('canvas');

    const frameState = event.frameState;
    const resolution = frameState.viewState.resolution;
    const viewportWidth = Number((frameState.size[0] * frameState.pixelRatio).toFixed());
    const viewportHeight = Number((frameState.size[1] * frameState.pixelRatio).toFixed());

    // Topmost canvas were the mask will be drawn.
    // We need to use it's size to compute the pixel ratio:
    //  - on high dpi devices, the size is multiplied by pixelRatio if the layer is a vector layer but not if it's a raster layer.
    //  - on low dpi devices, the size is never multiplied by pixelRatio.
    const canvas = canvases.item(canvases.length - 1);
    const pixelRatio = canvas.width / viewportWidth * frameState.pixelRatio;
    console.assert(pixelRatio == canvas.height / viewportHeight * frameState.pixelRatio);

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    const paperSizePoints = this.helper_.getPaperSize(this.getLayout_());
    const [paperSizePixelWidth, paperSizePixelHeight] = paperSizePoints.map(side =>
      side / POINTS_PER_METER * this.scale / resolution * pixelRatio);

    const minX = centerX - paperSizePixelWidth / 2;
    const minY = centerY - paperSizePixelHeight / 2;

    const context = canvas.getContext('2d');

    context.beginPath();

    // outer rectangle
    context.rect(0, 0, canvas.width, canvas.height);

    // inner rectangle
    context.rect(minX, minY, paperSizePixelWidth, paperSizePixelHeight);

    context.fillStyle = 'rgba(0, 5, 25, 0.5)';
    context.fill('evenodd');
  }


  /**
   * @private
   * @return {number}
   */
  getOptimalScale_() {
    const size = this.map.getSize();
    const resolution = this.map.getView().getResolution();
    const width = size[0] * resolution;
    const height = size[1] * resolution;
    return this.helper_.computeScale(this.getLayout_(), width, height);
  }


  /**
   * @export
   */
  print() {
    console.assert(this.scale);
    let title;
    if (this.inTracks) {
      gtag('event', 'track_print');
      title = this.trackService_.selectedTrack.properties.name;
    } else {
      gtag('event', 'general_print');
    }
    this.helper_.print(this.map, title, this.getLayout_(), this.scale);
  }
}

appBase.controller('appPrintController', PrintController);


/**
 * @return {angular.Directive} The directive specs.
 * @ngInject
 */
const appPrint = function() {
  return {
    restrict: 'E',
    controller: 'appPrintController',
    controllerAs: 'printCtrl',
    scope: {
      'parent': '@appParent',
      'inTracks': '=appInTracks'
    },
    bindToController: true,
    templateUrl: 'partials/print.html'
  };
};

appBase.directive('appPrint', appPrint);


export default appPrint;
